import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { BILLING_CHARGE_TYPES, BILLING_CHARGE_UNITS, billingChargeTypeLabel, billingChargeUnitLabel, companyOwnedWarehouses, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingMasterData, WarehousingStorageRate } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { ProductsService } from '../../order/product.service';
import { Products } from '../../order/models/product.model';
import { SessionStorageService } from '../../services/session-storage.service';

@Component({
  selector: 'app-warehousing-storage-rate-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, CheckboxModule,
    DatePickerModule, FieldsetModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule,
    PageFormSectionComponent, PageHeroComponent, PageShellComponent, ProgressBarModule, SelectModule, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './warehousing-storage-rate-form.component.html'
})
export class WarehousingStorageRateFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private productsService = inject(ProductsService);
  private sessionStorageService = inject(SessionStorageService);

  isLoading = false;
  isSaving = false;
  isEditMode = false;
  rateId?: number;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Storage Rate', 'Storage Rates', '/warehousing/setup/storage-rates');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  allWarehouses: WarehousingMasterData[] = [];
  warehouseOptions: WarehousingMasterData[] = [];
  masterData = masterDataMap();
  chargeTypes = BILLING_CHARGE_TYPES;
  chargeUnits = BILLING_CHARGE_UNITS;
  warehouseProducts: Products[] = [];

  rateForm = this.fb.group({
    crmClientId: [null as number | null, Validators.required],
    crmAccountId: [null as number | null],
    regionId: [null as number | null, Validators.required],
    warehouseId: [null as number | null, Validators.required],
    chargeType: ['STORAGE', Validators.required],
    chargeUnit: ['SQM'],
    productId: [null as number | null],
    variantId: [null as number | null],
    ratePerDay: [0, [Validators.required, Validators.min(0)]],
    currency: ['SAR'],
    effectiveFrom: [this.today()],
    effectiveTo: [null as Date | null],
    isActive: [true]
  });

  ngOnInit(): void {
    this.rateId = Number(this.route.snapshot.paramMap.get('id')) || undefined;
    this.isEditMode = !!this.rateId;
    this.loadInitialData();
    this.rateForm.get('productId')?.valueChanges.subscribe(productId => this.applyProductDefaults(productId));
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      rates: this.warehousingService.getStorageRates(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE')
      ,
      products: this.productsService.getLogisticsCatalog(
        this.sessionStorageService.getCompany()!.id,
        'WAREHOUSE_BILLING',
        undefined,
        this.rateForm.value.currency || 'SAR')
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.REGION = response.regions.data || [];
        this.allWarehouses = response.warehouses.data || [];
        this.warehouseOptions = companyOwnedWarehouses(this.allWarehouses);
        this.warehouseProducts = response.products || [];
        const rate = (response.rates.data || []).find(row => row.id === this.rateId);
        if (rate) {
          const chargeType = rate.chargeType || this.legacyChargeType(rate.metric);
          const chargeUnit = rate.chargeUnit || this.legacyChargeUnit(rate.metric);
          this.rateForm.patchValue({
            ...rate,
            chargeType,
            chargeUnit,
            effectiveFrom: this.toDate(rate.effectiveFrom),
            effectiveTo: this.toDate(rate.effectiveTo)
          } as any);
          this.ensureAllowedWarehouseSelection();
        }
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  saveRate(): void {
    if (this.isSaving) {
      return;
    }
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      return;
    }
    const formValue = this.rateForm.getRawValue();
    if (formValue.chargeType === 'STORAGE' && !formValue.chargeUnit) {
      this.rateForm.get('chargeUnit')?.setErrors({ required: true });
      this.rateForm.markAllAsTouched();
      return;
    }
    const payload = {
      id: this.rateId,
      ...formValue,
      metric: this.legacyMetric(formValue.chargeType, formValue.chargeUnit),
      chargeUnit: formValue.chargeType === 'STORAGE' ? formValue.chargeUnit : undefined,
      effectiveFrom: this.formatDate(formValue.effectiveFrom),
      effectiveTo: this.formatDate(formValue.effectiveTo)
    } as WarehousingStorageRate;
    this.isSaving = true;
    this.warehousingService.saveStorageRate(payload).pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Rate Saved', detail: 'Storage billing rate was saved.' });
      this.router.navigate(['/warehousing/setup/storage-rates/view', resp.data?.id || this.rateId]);
    });
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  setChargeType(chargeType: string): void {
    this.rateForm.patchValue({
      chargeType,
      chargeUnit: chargeType === 'STORAGE' ? this.rateForm.value.chargeUnit || 'SQM' : null
    });
  }

  setChargeUnit(chargeUnit: string): void {
    this.rateForm.patchValue({ chargeType: 'STORAGE', chargeUnit });
  }

  isSelectedChargeType(chargeType: string): boolean {
    return this.rateForm.value.chargeType === chargeType;
  }

  isSelectedChargeUnit(chargeUnit: string): boolean {
    return this.rateForm.value.chargeType === 'STORAGE' && this.rateForm.value.chargeUnit === chargeUnit;
  }

  selectedChargeLabel(): string {
    if (this.rateForm.value.chargeType === 'STORAGE') {
      return this.chargeUnitLabel(this.rateForm.value.chargeUnit);
    }
    return this.chargeTypeLabel(this.rateForm.value.chargeType);
  }

  isStorageCharge(): boolean {
    return this.rateForm.value.chargeType === 'STORAGE';
  }

  productVariants() {
    return this.warehouseProducts.find(product => product.productID === this.rateForm.value.productId)
      ?.productVariants || [];
  }

  selectedProduct(): Products | undefined {
    return this.warehouseProducts.find(product => product.productID === this.rateForm.value.productId);
  }

  private applyProductDefaults(productId: number | null | undefined): void {
    const product = this.warehouseProducts.find(item => item.productID === productId);
    if (!product) {
      this.rateForm.patchValue({ variantId: null }, { emitEvent: false });
      return;
    }
    const usage = product.logisticsUsages?.find(item =>
      item.usageModule === 'WAREHOUSE_BILLING' && item.active !== false);
    const variant = product.productVariants?.find(item => item.variantID === usage?.defaultVariantId)
      || (product.productVariants?.length === 1 ? product.productVariants[0] : undefined);
    this.rateForm.patchValue({
      chargeType: usage?.operationalCode || this.rateForm.value.chargeType,
      variantId: variant?.variantID || null,
      ratePerDay: variant?.sellingRateAdjustment ?? product.sellingRate ?? product.standardRate ?? 0,
      currency: product.currencyCode || this.rateForm.value.currency
    }, { emitEvent: false });
  }

  chargeTypeLabel = billingChargeTypeLabel;
  chargeUnitLabel = billingChargeUnitLabel;

  discard(): void { this.router.navigate(['/warehousing/setup/storage-rates']); }

  private ensureAllowedWarehouseSelection(): void {
    const warehouseId = this.rateForm.get('warehouseId')?.value;
    if (!warehouseId) {
      return;
    }
    if (!this.warehouseOptions.some(row => row.id === warehouseId)) {
      this.rateForm.patchValue({ warehouseId: null }, { emitEvent: false });
    }
  }

  private today(): Date { return new Date(); }

  private toDate(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
  }

  private formatDate(value: string | Date | null | undefined): string | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'string') {
      return value;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private legacyMetric(chargeType: string | null | undefined, chargeUnit: string | null | undefined): string {
    return chargeType === 'STORAGE' ? chargeUnit || 'SQM' : chargeType || 'OTHER';
  }

  private legacyChargeType(metric: string | null | undefined): string {
    return metric === 'SQM' || metric === 'CBM' ? 'STORAGE' : metric || 'STORAGE';
  }

  private legacyChargeUnit(metric: string | null | undefined): string | null {
    return metric === 'SQM' || metric === 'CBM' ? metric : null;
  }
}
